import React, { useCallback, useEffect, useState } from "react";
import { Box, Typography, Paper, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, LinearProgress, Icon, useTheme , Theme} from "@material-ui/core";
import { makeStyles } from '@material-ui/core/styles';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import StatusIndicator from './StatusIndicator';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import WarningIcon from '@material-ui/icons/Warning';
import Chip from "@mui/material/Chip";
import QualityStatus from "./QualityStatus";
import Button from "@mui/material/Button";
import { EntityDTO } from "../../DTO/Entity";

interface DataQualityResult {
  id: string;
  name: string;
  ruleLabel: string;
  criticality: string;
  lastRunId: string | null;
  lastRunTimestamp: number | null;
  lastRunStatus: string | null;
  lastRunResult: string | null;
  lastRunsMonitorRuns: number;
  lastRunsMonitorSuccess: number;
  lastRunsMonitorFailed: number;
  lastRunsMonitorAttentionRequired: number;
  lastRunsMonitorAttentionTechicalError: number;
  lastRunsStatuses: {
    timestamp: number;
    status: string;
    result: string;
  }[];
}

const mockData: DataQualityResult[] = [
// Add more mock data as needed
];

const calculatePieData = (data: DataQualityResult[], theme: Theme) => {
  const successCount = data.filter(item => item.lastRunStatus === "SUCCESS").length;
  const failedCount = data.filter(item => item.lastRunStatus === "FAILED").length;
  const attentionRequiredCount = data.filter(item => item.lastRunStatus === "ATTENTION REQUIRED").length;
  const technicalErrorCount = data.filter(item => item.lastRunStatus === "TECHNICAL ERROR").length;
  const notRunCount = data.filter(item => item.lastRunStatus == null).length;


  return [
    { name: 'Success', value: successCount, color:  theme.palette.success.main },   
    { name: 'Failed', value: failedCount, color: theme.palette.error.main },  
    { name: 'Attention Req.', value: attentionRequiredCount, color: theme.palette.warning.main },
    { name: 'Technical Err.', value: technicalErrorCount, color: theme.palette.warning.dark },
    { name: 'Not run', value: notRunCount, color: theme.palette.grey[300] }
  ];
};

const calculateSuccessByCriticality = (data: DataQualityResult[]) => {
  const criticalityMap: { [key: string]: { successCount: number; totalCount: number } } = {};

  data.forEach(item => {
    if (!criticalityMap[item.criticality]) {
      criticalityMap[item.criticality] = { successCount: 0, totalCount: 0 };
    }
    criticalityMap[item.criticality].totalCount += 1;
    if (item.lastRunStatus === "SUCCESS") {
      criticalityMap[item.criticality].successCount += 1;
    }
  });

  return Object.keys(criticalityMap).map(criticality => ({
    criticality,
    successRate: (criticalityMap[criticality].successCount / criticalityMap[criticality].totalCount) * 100,
  }));
};

const getCriticalityIcon = (criticality: string) => {
  switch (criticality) {
    case 'CRITICAL':
      return <KeyboardDoubleArrowUpIcon />;
    case 'HIGH':
      return <KeyboardDoubleArrowUpIcon />;
    case 'LOW':
      return <KeyboardArrowUpIcon />;
    default:
      return <KeyboardArrowUpIcon />;
  }
};

const useStyles = makeStyles((theme: Theme) => ({
  barColorPrimary: {
    backgroundColor: theme.palette.success.main,
  },
  colorPrimary: {
    backgroundColor: '#e0e0e0',
  },
  progressBarContainer: {
    width: '100%',
    maxWidth: 500,
    margin: '0 auto', // Center align the container
  },
  criticalityText: {
    width: 60, // Adjust this width as needed
    marginRight: 8,
  },
  icon: {
    marginRight: 16,
  },
  percentageText: {
    width: 50, // Adjust this width as needed
    textAlign: 'right',
  },
  subtitle: {
    color: 'grey',
    fontSize: '0.875rem',
  },
  severityReportCard: {
    height: 400, // Ensure the card has the same height as the "Quality status" card
    display: 'flex',
    flexDirection: 'column',
  },
  severityReportContent: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center', // Center-align the progress bars vertically
  },
  table: {
    minWidth: 650,
  },
  errorMessage: {
    color: 'grey',
  },
  refreshButton: {
    marginTop: theme.spacing(2),
  },
}));

interface DataQualityDashboardProps {
  entity: EntityDTO;
  token: string;
  forwardEndpoint: string;
}

const DataQualityDashboard : React.FC<DataQualityDashboardProps> = ({ entity, token, forwardEndpoint }) => {
  const theme = useTheme();
  const classes = useStyles();
  const [data, setData] = useState<DataQualityResult[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  
  const onPieEnter = (data: any, index: React.SetStateAction<number>) => {
    setActiveIndex(index);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {

      let apiUrl = forwardEndpoint;      
      if (!apiUrl) {
        apiUrl = "https://sifflet-mw.witboost.dev.fundation.digital.cloud.int/forward"
      }
      
      const body = JSON.stringify({
        "outputport": entity.op ,
      });

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body,
      });
      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }
      const result = await response.json();
      setData(result);
    } catch (error) {
      console.error("Error fetching data:", error);
      setData(mockData);
    } finally {
      setLoading(false);
    }
  }, [entity, token, forwardEndpoint]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const successByCriticality = calculateSuccessByCriticality(data);

  const STATUS_COLORS: { [key: string]: string } = {
    SUCCESS: theme.palette.success.main,  
    FAILED: theme.palette.error.main, 
    'ATTENTION REQUIRED': theme.palette.warning.main,
    'TECHNICAL ERROR': theme.palette.warning.dark,
    DEFAULT: '#D3D3D3', // Gray color for missing points
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'SUCCESS':
        return <CheckCircleIcon style={{ color: STATUS_COLORS.SUCCESS }} />;
      case 'FAILED':
        return <ErrorIcon style={{ color: STATUS_COLORS.FAILED }} />;
      case 'ATTENTION REQUIRED':
        return <WarningIcon style={{ color: STATUS_COLORS['ATTENTION REQUIRED'] }} />;
      case 'TECHNICAL ERROR':
        return <WarningIcon style={{ color: STATUS_COLORS['TECHNICAL ERROR'] }} />;
      default:
        return <HelpOutlineIcon style={{ color: theme.palette.grey[600] }} />;
    }
    };

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={11}>
          <Box display="flex" justifyContent="right" marginBottom={2}  className={classes.refreshButton}>
            <Button
              variant="contained"
              sx={{ backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText }}   
              onClick={fetchData}
              disabled={loading}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </Button>
          </Box>
        </Grid>
      </Grid>
      <Grid container spacing={3} justifyContent="center">
        <Grid item xs={12} md={5}>
          <Paper style={{ padding: 16, height: 400 }}>
            <Typography variant="h5">Quality status</Typography>
            <Typography className={classes.subtitle}>The percentage of successful monitors</Typography>
            <QualityStatus data={calculatePieData(data, theme)} activeIndex={activeIndex} onPieEnter={onPieEnter} theme={theme}/>
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper className={classes.severityReportCard} style={{ padding: 16 }}>
            <Typography variant="h5">Severity report</Typography>
            <Typography className={classes.subtitle}>The percentage of successful monitors, grouped by severity</Typography>
            <Box className={classes.severityReportContent}>
              {successByCriticality.map(item => (
                <Box key={item.criticality} display="flex" alignItems="center" mb={2} className={classes.progressBarContainer}>
                  <Icon className={classes.icon}>{getCriticalityIcon(item.criticality)}</Icon>
                  <Typography variant="body1" className={classes.criticalityText}>
                    {item.criticality}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={item.successRate}
                    style={{ flexGrow: 1, height: 10, marginLeft: 40, marginRight: 16 }}
                    classes={{ barColorPrimary: classes.barColorPrimary, colorPrimary: classes.colorPrimary }}
                  />
                  <Typography variant="body1" className={classes.percentageText}>{`${item.successRate.toFixed(2)}%`}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>
      <Grid container spacing={3}>
      <Grid item xs={12}>
        <TableContainer component={Paper}>
          <Table className={classes.table} aria-label="status table">
            <TableHead>
              <TableRow>
                <TableCell style={{ fontWeight: 'bold' }}>Monitor name</TableCell>
                <TableCell style={{ fontWeight: 'bold' }}>Label</TableCell>
                <TableCell style={{ fontWeight: 'bold' }}>Last run</TableCell>
                <TableCell style={{ fontWeight: 'bold' }}>Last status</TableCell>
                <TableCell style={{ fontWeight: 'bold' }}>History</TableCell>
                <TableCell style={{ fontWeight: 'bold' }}>Last error message</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((item) => (
                <TableRow key={item.id}>
                  <TableCell style={{ fontWeight: 'bold' }}>{item.name}</TableCell>
                  <TableCell>
                    <Chip
                      style={{ backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText }}
                      label={item.ruleLabel}
                    />
                  </TableCell>
                  <TableCell>
                  {item.lastRunTimestamp ? new Date(item.lastRunTimestamp).toLocaleString() : "Never"}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" color={theme.palette.grey[600]}>
                      {item.lastRunTimestamp ? (
                        <>
                          {getStatusIcon(item.lastRunStatus || 'DEFAULT')}
                          <Typography style={{ marginLeft: 8, fontSize: '0.875rem' }}>
                            {item.lastRunStatus
                              ?.toLowerCase()
                              .replace(/\b\w/g, (char) => char.toUpperCase())}
                          </Typography>
                        </>
                      ) : (
                        <HelpOutlineIcon style={{ color: theme.palette.grey[600] }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <StatusIndicator statuses={item.lastRunsStatuses} />
                    </Box>
                  </TableCell>
                  <TableCell className={classes.errorMessage} color={theme.palette.grey[600]}>{item.lastRunResult} </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  </Box>
  );
};

export default DataQualityDashboard;